import type { DeliveryAreaConfig } from "@/lib/delivery-area/types"
import type { DeliveryQuoteReason } from "@/lib/delivery-quote/fetch-delivery-quote"
import type { AddressQuote } from "@/hooks/use-delivery-quotes"
import { Address, ServiceArea } from "@/types"
import {
  getAddressServiceAreaStatus,
  isUsableServiceArea,
  pickDeliveryPolygon,
} from "../components/service-area"

/** One saved address measured against the merchant's delivery area. */
export type EvaluatedAddress = {
  address: Address
  deliverable: boolean
  price: number
  estimateMinutes: number
}

/** Prices and ETAs every saved address against the service area; empty when the area is unusable. */
export function evaluateAddresses(
  addresses: Address[],
  serviceArea: ServiceArea | undefined,
  merchantAddress: Address | undefined,
  deliveryAreaConfig?: DeliveryAreaConfig | null,
): EvaluatedAddress[] {
  if (!isUsableServiceArea(serviceArea, deliveryAreaConfig)) return []
  return addresses.map((address) => {
    const { availablePolygons, minPriceAvailable } = getAddressServiceAreaStatus(
      address,
      serviceArea,
      merchantAddress,
      deliveryAreaConfig,
    )
    const deliverable = availablePolygons.length > 0
    const chosenPolygon = pickDeliveryPolygon(availablePolygons, minPriceAvailable)
    return {
      address,
      deliverable,
      price: minPriceAvailable,
      estimateMinutes: chosenPolygon?.estimateDeliveryTime ?? 0,
    }
  })
}

/** A saved address priced for the service step: the core quote when it answered, the local preview otherwise. */
export type QuotedAddress = EvaluatedAddress & {
  pending: boolean
  reason: DeliveryQuoteReason | null
}

const STORE_REASONS: ReadonlyArray<DeliveryQuoteReason> = ["STORE_CLOSED", "STORE_PAUSED"]

/** Lets the core quote decide zone and fee per address; store-status reasons and missing quotes keep the local preview. */
export function applyDeliveryQuotes(
  addresses: Address[],
  preview: EvaluatedAddress[],
  quotes: Record<string, AddressQuote>,
): QuotedAddress[] {
  const byId = new Map(preview.map((entry) => [entry.address.id, entry]))
  return addresses.map((address) => {
    const local = byId.get(address.id) ?? { address, deliverable: false, price: 0, estimateMinutes: 0 }
    const entry = quotes[address.id]
    if (entry?.status === "loading") {
      return { ...local, deliverable: false, pending: true, reason: null }
    }
    if (entry?.status === "ready" && !STORE_REASONS.includes(entry.quote.reason)) {
      return {
        address,
        deliverable: entry.quote.deliverable,
        price: entry.quote.feeCents / 100,
        estimateMinutes: entry.quote.etaMinutes,
        pending: false,
        reason: entry.quote.reason,
      }
    }
    return { ...local, pending: false, reason: null }
  })
}
